import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AccessTime,
} from "@mui/icons-material";
import { bookingService } from "../services/bookingService";
import { dateUtils } from "../utils/dateUtils";

const defaultSlot = {
  dayOfWeek: 1, // Monday
  startTime: "09:00",
  endTime: "09:15",
  lessonTypeId: "",
  maxBookings: 0,
};

const timeOptions = [];
for (let hour = 8; hour <= 22; hour++) {
  for (let minute = 0; minute < 60; minute += 15) {
    const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    timeOptions.push(time);
  }
}
timeOptions.push("23:00");

function ScheduleManager() {
  const [weeklySchedule, setWeeklySchedule] = useState([]);
  const [lessonTypes, setLessonTypes] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [modalSlot, setModalSlot] = useState(defaultSlot);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [schedule, types] = await Promise.all([
        bookingService.getWeeklySchedule(),
        bookingService.getLessonTypes(),
      ]);
      setWeeklySchedule(schedule);
      setLessonTypes(types);
    } catch (error) {
      console.error("Error loading schedule data:", error);
      setError("Errore nel caricamento dei dati del palinsesto");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setModalSlot(defaultSlot);
    setModalEdit(false);
    setModalOpen(true);
    setError("");
    setSuccess("");
  };

  const handleEdit = (slot) => {
    setModalSlot({ ...slot });
    setModalEdit(true);
    setModalOpen(true);
    setError("");
    setSuccess("");
  };

  const handleClose = () => {
    setModalOpen(false);
    setModalSlot(defaultSlot);
    setError("");
    setSuccess("");
  };

  const handleInputChange = (field, value) => {
    setModalSlot((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };

      // Auto-set end time when start time changes
      if (field === 'startTime') {
        const [hours, minutes] = value.split(':').map(Number);
        const totalMinutes = hours * 60 + minutes + 15; // Add 15 minutes
        const endHours = Math.floor(totalMinutes / 60);
        const endMinutes = totalMinutes % 60;
        const endTime = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
        updated.endTime = endTime;
      }

      return updated;
    });
  };

  const validateForm = () => {
    if (!modalSlot.lessonTypeId) {
      setError("Seleziona un tipo di lezione");
      return false;
    }

    if (modalSlot.startTime >= modalSlot.endTime) {
      setError("L'orario di inizio deve essere precedente a quello di fine");
      return false;
    }

    if (modalSlot.maxBookings < 0) {
      setError("Il numero massimo di prenotazioni non può essere negativo");
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError("");

      if (modalEdit) {
        await bookingService.updateWeeklySlot(modalSlot.id, modalSlot);
        setSuccess("Slot aggiornato con successo!");
      } else {
        await bookingService.createWeeklySlot(modalSlot);
        setSuccess("Slot creato con successo!");
      }

      await loadData();
      handleClose();
    } catch (error) {
      console.error("Error saving slot:", error);
      setError("Errore nel salvataggio dello slot");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo slot?")) {
      return;
    }

    try {
      setLoading(true);
      await bookingService.deleteWeeklySlot(id);
      await loadData();
      setSuccess("Slot eliminato con successo!");
    } catch (error) {
      console.error("Error deleting slot:", error);
      setError("Errore nell'eliminazione dello slot");
    } finally {
      setLoading(false);
    }
  };

  const getLessonTypeName = (id) => {
    const lessonType = lessonTypes.find((type) => type.id === id);
    return lessonType ? lessonType.name : "Tipo sconosciuto";
  };

  const getLessonTypeColor = (id) => {
    const lessonType = lessonTypes.find((type) => type.id === id);
    return lessonType ? lessonType.color : "#2196F3";
  };

  const groupSlotsByDay = () => {
    const grouped = {};
    for (let day = 1; day <= 6; day++) {
      grouped[day] = weeklySchedule
        .filter((slot) => slot.dayOfWeek === day)
        .sort((a, b) => a.startTime.localeCompare(b.startTime));
    }
    return grouped;
  };

  const slotsByDay = groupSlotsByDay();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Gestione Palinsesto Settimanale</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          disabled={loading}
        >
          Aggiungi Slot
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {lessonTypes.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Prima di creare slot, devi configurare almeno un tipo di lezione.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Giorno</strong></TableCell>
              <TableCell><strong>Orario</strong></TableCell>
              <TableCell><strong>Tipo di Lezione</strong></TableCell>
              <TableCell><strong>Max Prenotazioni</strong></TableCell>
              <TableCell><strong>Azioni</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(slotsByDay).map(([dayNum, daySlots]) =>
              daySlots.length > 0 ? (
                daySlots.map((slot, index) => (
                  <TableRow key={slot.id}>
                    {index === 0 && (
                      <TableCell rowSpan={daySlots.length}>
                        <Typography fontWeight="bold">
                          {dateUtils.getDayOfWeekName(parseInt(dayNum))}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip
                        icon={<AccessTime />}
                        label={`${slot.startTime} - ${slot.endTime}`}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getLessonTypeName(slot.lessonTypeId)}
                        size="small"
                        sx={{
                          backgroundColor: getLessonTypeColor(slot.lessonTypeId),
                          color: "white",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={slot.maxBookings}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(slot)}
                        disabled={loading}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(slot.id)}
                        disabled={loading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow key={`empty-${dayNum}`}>
                  <TableCell>
                    <Typography fontWeight="bold" color="textSecondary">
                      {dateUtils.getDayOfWeekName(parseInt(dayNum))}
                    </Typography>
                  </TableCell>
                  <TableCell colSpan={4}>
                    <Typography color="textSecondary" fontStyle="italic">
                      Nessuno slot configurato
                    </Typography>
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {modalEdit ? "Modifica Slot" : "Nuovo Slot"}
        </DialogTitle>

        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Giorno della Settimana</InputLabel>
              <Select
                value={modalSlot.dayOfWeek}
                onChange={(e) => handleInputChange("dayOfWeek", e.target.value)}
                label="Giorno della Settimana"
              >
                {[1, 2, 3, 4, 5, 6].map((day) => (
                  <MenuItem key={day} value={day}>
                    {dateUtils.getDayOfWeekName(day)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box display="flex" gap={2} mb={2}>
              <FormControl fullWidth>
                <InputLabel>Ora Inizio</InputLabel>
                <Select
                  value={modalSlot.startTime}
                  onChange={(e) => handleInputChange("startTime", e.target.value)}
                  label="Ora Inizio"
                >
                  {timeOptions.map((time) => (
                    <MenuItem key={time} value={time}>
                      {time}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Ora Fine</InputLabel>
                <Select
                  value={modalSlot.endTime}
                  onChange={(e) => handleInputChange("endTime", e.target.value)}
                  label="Ora Fine"
                >
                  {timeOptions.map((time) => (
                    <MenuItem key={time} value={time}>
                      {time}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Tipo di Lezione</InputLabel>
              <Select
                value={modalSlot.lessonTypeId}
                onChange={(e) => handleInputChange("lessonTypeId", e.target.value)}
                label="Tipo di Lezione"
                required
              >
                {lessonTypes.map((lessonType) => (
                  <MenuItem key={lessonType.id} value={lessonType.id}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          backgroundColor: lessonType.color,
                          borderRadius: 1,
                        }}
                      />
                      {lessonType.name} ({lessonType.duration} min)
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Numero Massimo di Prenotazioni"
              type="number"
              value={modalSlot.maxBookings}
              onChange={(e) => handleInputChange("maxBookings", parseInt(e.target.value) || 0)}
              required
              InputProps={{ inputProps: { min: 0, max: 10 } }}
              helperText="Quante prenotazioni contemporanee sono permesse per questo slot (0 = solo visualizzazione, non prenotabile)"
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            startIcon={<CancelIcon />}
            disabled={loading}
          >
            Annulla
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            {loading ? "Salvataggio..." : "Salva"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ScheduleManager;